import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  arrayUnion,
  limit,
  getDoc,
  orderBy,
  startAfter,
  getDocsFromCache
} from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';

// Sistema de caché para mejorar el rendimiento
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos en milisegundos

// Constantes para control de concurrencia
const MAX_CONCURRENT_REQUESTS = 3; // Máximo número de consultas simultáneas
const REQUEST_DELAY = 100; // Delay entre consultas en ms

// Control de consultas concurrentes
let activeRequests = 0;
const requestQueue = [];

/**
 * Función para controlar las consultas concurrentes
 */
const executeWithConcurrencyControl = async (requestFunction) => {
  return new Promise((resolve, reject) => {
    const executeRequest = async () => {
      if (activeRequests >= MAX_CONCURRENT_REQUESTS) {
        // Si hay demasiadas consultas activas, encolar
        requestQueue.push({ executeRequest, resolve, reject });
        return;
      }

      activeRequests++;
      
      try {
        // Añadir delay entre consultas
        if (activeRequests > 1) {
          await new Promise(resolve => setTimeout(resolve, REQUEST_DELAY));
        }

        const result = await requestFunction();
        resolve(result);
      } catch (error) {
        reject(error);
      } finally {
        activeRequests--;
        
        // Procesar siguiente consulta en la cola
        if (requestQueue.length > 0) {
          const nextRequest = requestQueue.shift();
          nextRequest.executeRequest();
        }
      }
    };

    executeRequest();
  });
};

/**
 * Función para ejecutar consultas con retry y backoff exponencial
 */
const executeWithRetry = async (requestFunction, maxRetries = 3) => {
  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await executeWithConcurrencyControl(requestFunction);
    } catch (error) {
      lastError = error;
      
      // Si es un error de "Too many outstanding requests", esperar más tiempo
      if (error.code === 'resource-exhausted' || error.message.includes('Too many outstanding requests')) {
        const delay = Math.min(1000 * Math.pow(2, attempt), 10000); // Backoff exponencial, máximo 10s
        console.log(`Error de concurrencia, reintentando en ${delay}ms (intento ${attempt + 1}/${maxRetries + 1})`);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      
      // Para otros errores, no reintentar
      throw error;
    }
  }
  
  throw lastError;
};

// Función para generar claves de caché
const generateCacheKey = (serviceId, operation, params = {}) => {
  const paramsString = Object.keys(params).length > 0 
    ? JSON.stringify(params) 
    : '';
  return `${serviceId}_${operation}_${paramsString}`;
};

// Función para verificar si el caché es válido
const isCacheValid = (cacheEntry) => {
  if (!cacheEntry) return false;
  const now = Date.now();
  return (now - cacheEntry.timestamp) < CACHE_DURATION;
};

// Función para obtener datos del caché
const getFromCache = (key) => {
  const cacheEntry = cache.get(key);
  if (isCacheValid(cacheEntry)) {
    console.log(`Cache hit: ${key}`);
    return cacheEntry.data;
  }
  if (cacheEntry) {
    console.log(`Cache expired: ${key}`);
    cache.delete(key);
  }
  return null;
};

// Función para guardar datos en caché
const setCache = (key, data) => {
  cache.set(key, {
    data,
    timestamp: Date.now()
  });
  console.log(`Cache set: ${key}`);
};

// Función para limpiar caché específico
const clearCache = (serviceId = null) => {
  if (serviceId) {
    // Limpiar solo el caché del servicio específico
    for (const [key] of cache) {
      if (key.startsWith(`${serviceId}_`)) {
        cache.delete(key);
      }
    }
    console.log(`Cache cleared for service: ${serviceId}`);
  } else {
    // Limpiar todo el caché
    cache.clear();
    console.log('All cache cleared');
  }
};

// Función para obtener datos con caché
const getDataWithCache = async (key, fetchFunction) => {
  // Intentar obtener del caché primero
  const cachedData = getFromCache(key);
  if (cachedData) {
    return cachedData;
  }

  // Si no está en caché, obtener de Firestore
  try {
    const data = await fetchFunction();
    setCache(key, data);
    return data;
  } catch (error) {
    console.error('Error fetching data:', error);
    throw error;
  }
};


export const fetchAdmins = async (currentService) => {
  const adminQuery = query(collection(db, 'servicios', currentService.id, 'admins'));
  const adminSnapshot = await getDocs(adminQuery);
  return adminSnapshot.docs.map((doc) => {
    return { id: doc.id, ...doc.data() };
  });
};

// Función paginada para obtener admins con caché
export const fetchAdminsPaginados = async (currentService, pageSize = 50, lastDoc = null, filtros = {}) => {
  try {
    // Generar clave de caché
    const cacheKey = generateCacheKey(currentService.id, 'admins_paginados', {
      pageSize,
      lastDocId: lastDoc?.id || null,
      filtros
    });

    // Intentar obtener del caché primero (solo para la primera página)
    if (!lastDoc) {
      const cachedData = getFromCache(cacheKey);
      if (cachedData) {
        return cachedData;
      }
    }

    let q = query(
      collection(db, 'servicios', currentService.id, 'admins'),
      orderBy('nombre', 'asc'),
      limit(pageSize)
    );

    // Aplicar filtros si existen
    if (filtros.eliminado !== undefined) {
      q = query(
        collection(db, 'servicios', currentService.id, 'admins'),
        where('eliminado', '==', filtros.eliminado),
        orderBy('nombre', 'asc'),
        limit(pageSize)
      );
    }

    // Aplicar startAfter si hay un documento anterior
    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }

    const snapshot = await getDocs(q);
    const admins = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        nombre: data.nombre || data.nombreCliente || `Usuario ${doc.id}`,
        apellido: data.apellido || '',
        correo: data.correo || '',
        tipoUsuario: data.tipoUsuario || [],
        clientes: data.clientes || [],
        tiendas: data.tiendas || [],
        eliminado: data.eliminado || false,
        ...data
      };
    });

    const hasMore = snapshot.docs.length === pageSize;
    const lastVisible = snapshot.docs[snapshot.docs.length - 1];

    const result = {
      admins,
      hasMore,
      lastDoc: lastVisible
    };

    // Guardar en caché solo la primera página
    if (!lastDoc) {
      setCache(cacheKey, result);
    }

    return result;
  } catch (error) {
    console.error('Error fetching admins:', error);
    throw error;
  }
};


export const fetchClientesYTiendas = async (currentService) => {
  const clientesRef = collection(db, 'servicios', currentService.id, 'clientes');
  const tiendasRef = collection(db, 'servicios', currentService.id, 'tiendas');

  const [clientesSnapshot, tiendasSnapshot] = await Promise.all([
    getDocs(clientesRef),
    getDocs(tiendasRef)
  ]);

  const clientesData = clientesSnapshot.docs
    .filter(doc => !doc.data().borrado) // Filtrar los documentos con borrado igual a false
    .map(doc => ({ id: doc.id, ...doc.data() }));

  const tiendasData = tiendasSnapshot.docs
    .filter(doc => !doc.data().borrado) // Filtrar los documentos con borrado igual a false
    .map(doc => ({ id: doc.id, ...doc.data() }));

  return [clientesData, tiendasData];
};


export const addNewUser = async (newUserData, currentService) => {
  const { user } = await createUserWithEmailAndPassword(auth, newUserData.correo, newUserData.password);
  const usuarioDoc = doc(db, 'servicios', currentService.id, 'admins');
  await addDoc(usuarioDoc, {
    correo: newUserData.correo,
    nombre: newUserData.nombre,
    tipoUsuario: newUserData.tipoUsuario,
    userId: user.uid
  });
  return user.uid; // Devuelve el ID del nuevo usuario
};


export const updateClientesYTiendas = async (userId, tipoUsuario, newUserData, currentService) => {
  const esAdministrativo = tipoUsuario.includes('administrativo');
  const esInspector = tipoUsuario.includes('inspector');

  const updateRef = async (refPath, field, value) => {
    const ref = doc(db, refPath);
    await updateDoc(ref, { [field]: arrayUnion(value) });
  };

  for (const clienteId of newUserData.clientes) {
    if (esAdministrativo) {
      await updateRef(`servicios/${currentService.id}/clientes/${clienteId}`, 'administrativos', userId);
    }
    if (esInspector) {
      await updateRef(`servicios/${currentService.id}/clientes/${clienteId}`, 'inspectores', userId);
    }
  }

  for (const tiendaId of newUserData.tiendas) {
    if (esAdministrativo) {
      await updateRef(`servicios/${currentService.id}/tiendas/${tiendaId}`, 'administrativos', userId);
    }
    if (esInspector) {
      await updateRef(`servicios/${currentService.id}/tiendas/${tiendaId}`, 'inspectores', userId);
    }
  }
};

export const fetchUserData = async (currentUser, currentService) => {
  if (currentUser && currentService) {
    try {
      // Generar clave de caché
      const cacheKey = generateCacheKey(currentService.id, 'user_data', {
        userEmail: currentUser.email
      });

      // Intentar obtener del caché primero
      const cachedData = getFromCache(cacheKey);
      if (cachedData) {
        return cachedData;
      }

      const adminQuery = query(
        collection(db, 'servicios', currentService.id, 'admins'),
        where('correo', '==', currentUser.email),
        limit(1)
      );
      const adminSnapshot = await getDocs(adminQuery);

      if (!adminSnapshot.empty) {
        const adminData = adminSnapshot.docs[0].data();
        const adminId = adminSnapshot.docs[0].id;
        const result = { ...adminData, id: adminId };
        
        // Guardar en caché
        setCache(cacheKey, result);
        
        return result;
      } else {
        throw new Error("No se encontraron datos del administrador.");
      }
    } catch (error) {
      throw error;
    }
  }
};

// Función para obtener clientes por sus IDs (usa adminData.clientes como fuente de verdad)
// Necesaria porque updateClientesYTiendas está desactivada y los arrays administrativos/inspectores
// en los documentos de cliente pueden estar desincronizados
export const fetchClientesPorIds = async (clientesIds, servicio, adminId = null) => {
  if (!servicio?.id || !clientesIds?.length) return [];

  const cacheKey = `clientes_por_ids_${servicio.id}_${adminId || 'anon'}_${clientesIds.length}`;
  const cachedData = getFromCache(cacheKey);
  if (cachedData) return cachedData;

  const clientesRef = collection(db, 'servicios', servicio.id, 'clientes');
  const BATCH_SIZE = 50;
  const clientes = [];

  for (let i = 0; i < clientesIds.length; i += BATCH_SIZE) {
    const batch = clientesIds.slice(i, i + BATCH_SIZE);
    const promises = batch.map((clienteId) => getDoc(doc(clientesRef, clienteId)));
    const docs = await Promise.all(promises);

    docs.forEach((docSnap) => {
      if (docSnap.exists() && !docSnap.data().borrado) {
        clientes.push({ id: docSnap.id, ...docSnap.data() });
      }
    });
  }

  setCache(cacheKey, clientes);
  return clientes;
};

// Función para obtener clientes basada en el ID del administrador con caché
export const fetchClientesPorAdministrativo = async (adminId, servicio) => {
  console.log('fetchClientesPorAdministrativo llamado con:', { adminId, servicioId: servicio?.id });
  
  // Generar clave de caché
  const cacheKey = `clientes_administrativo_${servicio.id}_${adminId}`;
  
  // Intentar obtener del caché primero
  const cachedData = getFromCache(cacheKey);
  if (cachedData) {
    console.log('Usando datos en caché para administrativo');
    return cachedData;
  }
  
  const clientesRef = collection(db, 'servicios', servicio.id, 'clientes');
  const q = query(clientesRef, where('administrativos', 'array-contains', adminId));
  const querySnapshot = await getDocs(q);
  
  console.log(`Encontrados ${querySnapshot.size} clientes para administrativo ${adminId}`);
  
  const clientes = [];
  querySnapshot.forEach((doc) => {
    const clienteData = { id: doc.id, ...doc.data() };
    console.log('Cliente encontrado:', clienteData);
    clientes.push(clienteData);
  });
  
  console.log('Clientes finales:', clientes);
  
  // Guardar en caché por 5 minutos
  setCache(cacheKey, clientes);
  
  return clientes;
};

// Función para obtener clientes basada en el ID del inspector con caché
export const fetchClientesPorInspector = async (adminId, servicio) => {
  console.log('fetchClientesPorInspector llamado con:', { adminId, servicioId: servicio?.id });
  
  // Generar clave de caché
  const cacheKey = `clientes_inspector_${servicio.id}_${adminId}`;
  
  // Intentar obtener del caché primero
  const cachedData = getFromCache(cacheKey);
  if (cachedData) {
    console.log('Usando datos en caché para inspector');
    return cachedData;
  }
  
  const clientesRef = collection(db, 'servicios', servicio.id, 'clientes');
  const q = query(clientesRef, where('inspectores', 'array-contains', adminId));
  const querySnapshot = await getDocs(q);
  
  console.log(`Encontrados ${querySnapshot.size} clientes para inspector ${adminId}`);
  
  const clientes = [];
  querySnapshot.forEach((doc) => {
    const clienteData = { id: doc.id, ...doc.data() };
    console.log('Cliente encontrado:', clienteData);
    clientes.push(clienteData);
  });
  
  console.log('Clientes finales:', clientes);
  
  // Guardar en caché por 5 minutos
  setCache(cacheKey, clientes);
  
  return clientes;
};

export const fetchTiendasPorCampoCliente = async (clientesIds, servicio) => {
  console.log('fetchTiendasPorCampoCliente llamado con:', { clientesIds, servicioId: servicio?.id });
  
  if (!servicio?.id) {
    console.error('No hay servicio válido proporcionado');
    return [];
  }
  
  const tiendasRef = collection(db, 'servicios', servicio.id, 'tiendas');
  let tiendas = [];

  // Función externa para comparar tiendas por su ID
  const isTiendaDuplicada = (tiendaId) => tiendas.some(tienda => tienda.id === tiendaId);
  
  // Realizar una consulta por cada clienteId y combinar los resultados
  for (const clienteId of clientesIds) {
    console.log('Consultando tiendas para cliente:', clienteId);
    const q = query(tiendasRef, where('cliente', '==', clienteId));
    const querySnapshot = await getDocs(q);
    console.log(`Encontradas ${querySnapshot.size} tiendas para cliente ${clienteId}`);
    
    querySnapshot.forEach((doc) => {
      const tiendaData = { id: doc.id, ...doc.data() };
      console.log('Tienda encontrada:', tiendaData);
      console.log('Campo cliente de la tienda:', tiendaData.cliente, 'Tipo:', typeof tiendaData.cliente);
      // Asegurarse de no incluir tiendas duplicadas
      if (!isTiendaDuplicada(doc.id)) {
        tiendas.push(tiendaData);
      }
    });
  }

  // Filtrar tiendas que tienen 'borrado' igual a true
  const tiendasSinBorrar = tiendas.filter(tienda => tienda.borrado !== true);
  console.log(`Total de tiendas encontradas: ${tiendas.length}, después de filtrar borradas: ${tiendasSinBorrar.length}`);

  return tiendasSinBorrar;
};


export const fetchAdminData = async (currentUser, currentService) => {
  if (currentUser && currentService) {
    try {
      const adminQuery = query(
        collection(db, 'servicios', currentService.id, 'admins'),
        where('correo', '==', currentUser.email),
        limit(1)
      );
      const adminSnapshot = await getDocs(adminQuery);

      if (!adminSnapshot.empty) {
        const adminData = adminSnapshot.docs[0].data();
        return { ...adminData, id: adminSnapshot.docs[0].id };
      } else {
        throw new Error("No se encontraron datos del administrador.");
      }
    } catch (error) {
      throw error;
    }
  }
};

export const fetchAOtherdminData = async (adminId, currentService) => {
  if (adminId && currentService) {
    try {
      // Generar clave de caché
      const cacheKey = generateCacheKey(currentService.id, 'admin_data', {
        adminId
      });

      // Intentar obtener del caché primero
      const cachedData = getFromCache(cacheKey);
      if (cachedData) {
        return cachedData;
      }

      const adminRef = doc(db, 'servicios', currentService.id, 'admins', adminId);
      const adminSnapshot = await getDoc(adminRef);

      if (adminSnapshot.exists()) {
        const adminData = adminSnapshot.data();
        const result = { ...adminData, id: adminSnapshot.id };
        
        // Guardar en caché
        setCache(cacheKey, result);
        
        return result;
      } else {
        throw new Error("No se encontraron datos del administrador.");
      }
    } catch (error) {
      throw error;
    }
  }
};

// Función optimizada para cargar clientes y tiendas con caché
export const fetchClientesYTiendasConCache = async (currentService) => {
  try {
    // Generar clave de caché
    const cacheKey = generateCacheKey(currentService.id, 'clientes_tiendas');

    // Intentar obtener del caché primero
    const cachedData = getFromCache(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    const clientesRef = collection(db, 'servicios', currentService.id, 'clientes');
    const tiendasRef = collection(db, 'servicios', currentService.id, 'tiendas');

    const [clientesSnapshot, tiendasSnapshot] = await Promise.all([
      getDocs(clientesRef),
      getDocs(tiendasRef)
    ]);

    const clientesData = clientesSnapshot.docs
      .filter(doc => !doc.data().borrado)
      .map(doc => ({ id: doc.id, ...doc.data() }));

    const tiendasData = tiendasSnapshot.docs
      .filter(doc => !doc.data().borrado)
      .map(doc => ({ id: doc.id, ...doc.data() }));

    const result = [clientesData, tiendasData];
    
    // Guardar en caché
    setCache(cacheKey, result);
    
    return result;
  } catch (error) {
    console.error('Error fetching clientes y tiendas:', error);
    throw error;
  }
};

// Función para obtener vigilantes filtrados por servicio asignado
export const fetchVigilantesPorServicio = async (servicio) => {
  if (!servicio?.id) return [];
  
  try {
    const vigilantesRef = collection(db, 'servicios', servicio.id, 'vigilantes');
    const querySnapshot = await getDocs(vigilantesRef);
    
    const vigilantesList = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // Asegurar que los campos de texto existan para evitar errores
        name: data.name || data.nombre || data.email || 'Sin nombre',
        email: data.email || '',
        nombre: data.nombre || data.name || '',
        apellidos: data.apellidos || '',
        // Crear un nombre de visualización consistente
        displayName: data.name || 
          (data.nombre && data.apellidos ? `${data.nombre} ${data.apellidos}` : 
           data.nombre || data.email || 'Sin nombre')
      };
    });
    
    return vigilantesList;
  } catch (error) {
    console.error('Error fetching vigilantes por servicio:', error);
    return [];
  }
};

// Nuevas funciones optimizadas para carga paginada
export const fetchClientesPaginados = async (currentService, pageSize = 50, lastDoc = null, filtros = {}) => {
  const clientesRef = collection(db, 'servicios', currentService.id, 'clientes');
  let q;
  
  // Construir la consulta base
  if (filtros.comunidad) {
    q = query(
      clientesRef, 
      where('comunidad', '==', filtros.comunidad),
      where('borrado', '==', false),
      orderBy('nombre'), 
      limit(pageSize)
    );
  } else if (filtros.provincia) {
    q = query(
      clientesRef, 
      where('provincia', '==', filtros.provincia),
      where('borrado', '==', false),
      orderBy('nombre'), 
      limit(pageSize)
    );
  } else {
    q = query(
      clientesRef, 
      where('borrado', '==', false),
      orderBy('nombre'), 
      limit(pageSize)
    );
  }
  
  if (lastDoc) {
    q = query(q, startAfter(lastDoc));
  }
  
  const snapshot = await getDocs(q);
  const clientes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  
  return {
    clientes,
    lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
    hasMore: snapshot.docs.length === pageSize
  };
};

export const fetchTiendasPaginadas = async (currentService, pageSize = 50, lastDoc = null, filtros = {}) => {
  const tiendasRef = collection(db, 'servicios', currentService.id, 'tiendas');
  let q;
  
  // Construir la consulta base
  if (filtros.cliente) {
    q = query(
      tiendasRef, 
      where('cliente', '==', filtros.cliente),
      where('borrado', '==', false),
      orderBy('numTienda'), 
      limit(pageSize)
    );
  } else {
    q = query(
      tiendasRef, 
      where('borrado', '==', false),
      orderBy('numTienda'), 
      limit(pageSize)
    );
  }
  
  if (lastDoc) {
    q = query(q, startAfter(lastDoc));
  }
  
  const snapshot = await getDocs(q);
  let tiendas = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  
  // Filtrar por término de búsqueda si existe (incluye localizacion)
  if (filtros.searchTerm && filtros.searchTerm.length >= 2) {
    const searchLower = filtros.searchTerm.toLowerCase();
    tiendas = tiendas.filter(tienda => 
      (tienda.numTienda && tienda.numTienda.toString().toLowerCase().includes(searchLower)) ||
      (tienda.nombreTienda && tienda.nombreTienda.toLowerCase().includes(searchLower)) ||
      (tienda.localizacion && tienda.localizacion.toLowerCase().includes(searchLower))
    );
  }
  
  return {
    tiendas,
    lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
    hasMore: snapshot.docs.length === pageSize
  };
};

// Función para buscar clientes por nombre (búsqueda optimizada)
export const buscarClientes = async (currentService, searchTerm, limit = 20) => {
  if (!searchTerm || searchTerm.length < 2) return [];
  
  const clientesRef = collection(db, 'servicios', currentService.id, 'clientes');
  const q = query(
    clientesRef,
    where('borrado', '==', false),
    orderBy('nombre'),
    limit(limit)
  );
  
  const snapshot = await getDocs(q);
  const clientes = snapshot.docs
    .map(doc => ({ id: doc.id, ...doc.data() }))
    .filter(cliente => 
      cliente.nombre && cliente.nombre.toLowerCase().includes(searchTerm.toLowerCase())
    );
  
  return clientes;
};

// Función para buscar tiendas por número, nombre o localización (búsqueda optimizada)
export const buscarTiendas = async (currentService, searchTerm, limitCount = 20) => {
  if (!searchTerm || searchTerm.length < 2) return [];
  
  const tiendasRef = collection(db, 'servicios', currentService.id, 'tiendas');
  const q = query(
    tiendasRef,
    where('borrado', '==', false),
    orderBy('numTienda'),
    limit(limitCount)
  );
  
  const snapshot = await getDocs(q);
  const searchLower = searchTerm.toLowerCase();
  const tiendas = snapshot.docs
    .map(doc => ({ id: doc.id, ...doc.data() }))
    .filter(tienda => 
      (tienda.numTienda && tienda.numTienda.toString().toLowerCase().includes(searchLower)) ||
      (tienda.nombreTienda && tienda.nombreTienda.toLowerCase().includes(searchLower)) ||
      (tienda.localizacion && tienda.localizacion.toLowerCase().includes(searchLower))
    );
  
  return tiendas;
};

// Función para obtener estadísticas rápidas
export const getEstadisticas = async (currentService) => {
  try {
    const clientesRef = collection(db, 'servicios', currentService.id, 'clientes');
    const tiendasRef = collection(db, 'servicios', currentService.id, 'tiendas');
    
    const [clientesSnapshot, tiendasSnapshot] = await Promise.all([
      getDocs(query(clientesRef, where('borrado', '==', false))),
      getDocs(query(tiendasRef, where('borrado', '==', false)))
    ]);
    
    return {
      totalClientes: clientesSnapshot.size,
      totalTiendas: tiendasSnapshot.size
    };
  } catch (error) {
    console.error('Error getting statistics:', error);
    return { totalClientes: 0, totalTiendas: 0 };
  }
};

// Exportar funciones de caché para uso en otros componentes
export { clearCache, getFromCache, setCache };

// Función para limpiar caché cuando se actualiza un usuario
export const clearUserCache = (serviceId, userId = null) => {
  if (userId) {
    // Limpiar caché específico del usuario
    const userCacheKey = generateCacheKey(serviceId, 'user_data', { userId });
    cache.delete(userCacheKey);
    console.log(`User cache cleared for: ${userId}`);
  } else {
    // Limpiar todo el caché del servicio
    clearCache(serviceId);
  }
};

// Función para limpiar caché cuando se actualiza un admin
export const clearAdminCache = (serviceId) => {
  // Limpiar caché de admins
  for (const [key] of cache) {
    if (key.includes('admins_paginados') && key.startsWith(`${serviceId}_`)) {
      cache.delete(key);
    }
  }
  console.log(`Admin cache cleared for service: ${serviceId}`);
};

// Función optimizada para cargar solo clientes y tiendas necesarios para EditAdmin
export const fetchClientesYTiendasOptimizado = async (currentService, adminData = null) => {
  try {
    // Generar clave de caché
    const cacheKey = generateCacheKey(currentService.id, 'clientes_tiendas_optimizado', {
      adminId: adminData?.id || null,
      clientesAsignados: adminData?.clientes?.length || 0
    });

    // Intentar obtener del caché primero
    const cachedData = getFromCache(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    let clientesData = [];
    let tiendasData = [];

    if (adminData && adminData.clientes && adminData.clientes.length > 0) {
      // Si el admin ya tiene clientes asignados, cargar solo esos y sus tiendas
      const clientesRef = collection(db, 'servicios', currentService.id, 'clientes');
      const tiendasRef = collection(db, 'servicios', currentService.id, 'tiendas');

      // Cargar solo los clientes asignados
      const clientesPromises = adminData.clientes.map(clienteId => 
        getDoc(doc(clientesRef, clienteId))
      );
      const clientesDocs = await Promise.all(clientesPromises);
      
      clientesData = clientesDocs
        .filter(doc => doc.exists() && !doc.data().borrado)
        .map(doc => ({ id: doc.id, ...doc.data() }));

      // Cargar solo las tiendas de los clientes asignados
      const tiendasPromises = adminData.clientes.map(clienteId => 
        getDocs(query(tiendasRef, where('cliente', '==', clienteId), where('borrado', '==', false)))
      );
      const tiendasSnapshots = await Promise.all(tiendasPromises);
      
      tiendasData = tiendasSnapshots.flatMap(snapshot => 
        snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      );
    } else {
      // Si no hay clientes asignados, cargar solo la primera página
      const [clientesResult, tiendasResult] = await Promise.all([
        fetchClientesPaginados(currentService, 50),
        fetchTiendasPaginadas(currentService, 50)
      ]);
      
      clientesData = clientesResult.clientes;
      tiendasData = tiendasResult.tiendas;
    }

    const result = [clientesData, tiendasData];
    
    // Guardar en caché
    setCache(cacheKey, result);
    
    return result;
  } catch (error) {
    console.error('Error fetching clientes y tiendas optimizado:', error);
    throw error;
  }
};

// Función para cargar más clientes cuando sea necesario
export const fetchMoreClientes = async (currentService, lastDoc, pageSize = 50) => {
  try {
    const result = await fetchClientesPaginados(currentService, pageSize, lastDoc);
    return result;
  } catch (error) {
    console.error('Error fetching more clientes:', error);
    throw error;
  }
};

// Función para cargar más tiendas cuando sea necesario
export const fetchMoreTiendas = async (currentService, lastDoc, pageSize = 50) => {
  try {
    const result = await fetchTiendasPaginadas(currentService, pageSize, lastDoc);
    return result;
  } catch (error) {
    console.error('Error fetching more tiendas:', error);
    throw error;
  }
};

// Función para buscar clientes específicos por nombre
export const buscarClientesEspecificos = async (currentService, searchTerm, limit = 20) => {
  if (!searchTerm || searchTerm.length < 2) return [];
  
  try {
    const clientesRef = collection(db, 'servicios', currentService.id, 'clientes');
    const q = query(
      clientesRef,
      where('borrado', '==', false),
      orderBy('nombre'),
      limit(limit)
    );
    
    const snapshot = await getDocs(q);
    const clientes = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(cliente => 
        cliente.nombre && cliente.nombre.toLowerCase().includes(searchTerm.toLowerCase())
      );
    
    return clientes;
  } catch (error) {
    console.error('Error searching clientes:', error);
    return [];
  }
};

// Función para buscar tiendas específicas por número o nombre
export const buscarTiendasEspecificas = async (currentService, searchTerm, limit = 20) => {
  if (!searchTerm || searchTerm.length < 2) return [];
  
  try {
    const tiendasRef = collection(db, 'servicios', currentService.id, 'tiendas');
    const q = query(
      tiendasRef,
      where('borrado', '==', false),
      orderBy('numTienda'),
      limit(limit)
    );
    
    const snapshot = await getDocs(q);
    const tiendas = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(tienda => 
        (tienda.numTienda && tienda.numTienda.toString().includes(searchTerm)) ||
        (tienda.nombreTienda && tienda.nombreTienda.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    
    return tiendas;
  } catch (error) {
    console.error('Error searching tiendas:', error);
    return [];
  }
};

// Función para cargar clientes de forma lazy y por demanda
export const fetchClientesLazy = async (currentService, adminData = null, pageSize = 20) => {
  try {
    let clientesData = [];
    
    if (adminData && adminData.clientes && adminData.clientes.length > 0) {
      // Si tiene muchos clientes asignados, cargar solo los primeros
      const clientesToLoad = adminData.clientes.slice(0, pageSize);
      
      const clientesRef = collection(db, 'servicios', currentService.id, 'clientes');
      const clientesPromises = clientesToLoad.map(clienteId => 
        getDoc(doc(clientesRef, clienteId))
      );
      const clientesDocs = await Promise.all(clientesPromises);
      
      clientesData = clientesDocs
        .filter(doc => doc.exists() && !doc.data().borrado)
        .map(doc => ({ id: doc.id, ...doc.data() }));
    } else {
      // Si no hay clientes asignados, cargar solo la primera página
      const result = await fetchClientesPaginados(currentService, pageSize);
      clientesData = result.clientes;
    }

    return {
      clientes: clientesData,
      hasMore: adminData?.clientes?.length > pageSize || false,
      totalCount: adminData?.clientes?.length || 0,
      loadedCount: clientesData.length
    };
  } catch (error) {
    console.error('Error fetching clientes lazy:', error);
    throw error;
  }
};

// Función para cargar tiendas de forma lazy y por demanda
export const fetchTiendasLazy = async (currentService, clientesIds = [], pageSize = 20) => {
  try {
    let tiendasData = [];
    
    if (clientesIds.length > 0) {
      // Si hay clientes específicos, cargar solo las tiendas de esos clientes
      const tiendasRef = collection(db, 'servicios', currentService.id, 'tiendas');
      
      // Cargar tiendas de los clientes especificados, limitando por página
      const tiendasPromises = clientesIds.slice(0, 5).map(clienteId => // Limitar a 5 clientes por vez
        getDocs(query(tiendasRef, where('cliente', '==', clienteId), where('borrado', '==', false), limit(pageSize)))
      );
      const tiendasSnapshots = await Promise.all(tiendasPromises);
      
      tiendasData = tiendasSnapshots.flatMap(snapshot => 
        snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      ).slice(0, pageSize); // Limitar el total
    } else {
      // Si no hay clientes específicos, cargar solo la primera página
      const result = await fetchTiendasPaginadas(currentService, pageSize);
      tiendasData = result.tiendas;
    }

    return {
      tiendas: tiendasData,
      hasMore: tiendasData.length === pageSize,
      totalCount: 0, // No calculamos el total para evitar consultas costosas
      loadedCount: tiendasData.length
    };
  } catch (error) {
    console.error('Error fetching tiendas lazy:', error);
    throw error;
  }
};

// Función para cargar más clientes específicos (para usuarios con muchos clientes)
export const fetchMoreClientesEspecificos = async (currentService, clientesIds, loadedCount, pageSize = 20) => {
  try {
    const startIndex = loadedCount;
    const endIndex = startIndex + pageSize;
    const clientesToLoad = clientesIds.slice(startIndex, endIndex);
    
    if (clientesToLoad.length === 0) {
      return {
        clientes: [],
        hasMore: false,
        loadedCount: loadedCount
      };
    }

    const clientesRef = collection(db, 'servicios', currentService.id, 'clientes');
    const clientesPromises = clientesToLoad.map(clienteId => 
      getDoc(doc(clientesRef, clienteId))
    );
    const clientesDocs = await Promise.all(clientesPromises);
    
    const nuevosClientes = clientesDocs
      .filter(doc => doc.exists() && !doc.data().borrado)
      .map(doc => ({ id: doc.id, ...doc.data() }));

    return {
      clientes: nuevosClientes,
      hasMore: endIndex < clientesIds.length,
      loadedCount: loadedCount + nuevosClientes.length
    };
  } catch (error) {
    console.error('Error fetching more clientes específicos:', error);
    throw error;
  }
};

// Función para cargar más tiendas específicas
export const fetchMoreTiendasEspecificas = async (currentService, clientesIds, loadedCount, pageSize = 20) => {
  try {
    const tiendasRef = collection(db, 'servicios', currentService.id, 'tiendas');
    
    // Calcular qué clientes necesitamos cargar
    // Si loadedCount es 0, empezamos desde el primer cliente
    // Si loadedCount > 0, calculamos cuántos clientes ya procesamos
    const clientesPorCarga = 5; // Cargar tiendas de 5 clientes por vez
    
    // Calcular cuántos clientes ya hemos procesado para servicios
    // Si loadedCount es 0, empezamos desde 0
    // Si loadedCount > 0, calculamos basándonos en cuántos servicios ya cargamos
    let startClienteIndex = 0;
    
    if (loadedCount > 0) {
      // Si ya cargamos servicios, calculamos cuántos clientes ya procesamos
      // Asumimos que cada cliente puede tener hasta pageSize servicios
      startClienteIndex = Math.floor(loadedCount / (pageSize * 0.8)); // Factor de 0.8 para ser conservador
    }
    
    // Asegurar que no excedamos el número total de clientes
    if (startClienteIndex >= clientesIds.length) {
      return {
        tiendas: [],
        hasMore: false,
        loadedCount: loadedCount
      };
    }
    
    const clientesToLoad = clientesIds.slice(startClienteIndex, startClienteIndex + clientesPorCarga);
    
    if (clientesToLoad.length === 0) {
      return {
        tiendas: [],
        hasMore: false,
        loadedCount: loadedCount
      };
    }

    console.log(`Loading services for clients: ${clientesToLoad.join(', ')} (startIndex: ${startClienteIndex})`);

    const tiendasPromises = clientesToLoad.map(clienteId => 
      getDocs(query(tiendasRef, where('cliente', '==', clienteId), where('borrado', '==', false), limit(pageSize)))
    );
    const tiendasSnapshots = await Promise.all(tiendasPromises);
    
    const nuevasTiendas = tiendasSnapshots.flatMap(snapshot => 
      snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    );

    console.log(`Loaded ${nuevasTiendas.length} new services from ${clientesToLoad.length} clients`);

    // Verificar si hay más clientes para procesar
    const hasMore = startClienteIndex + clientesPorCarga < clientesIds.length;

    return {
      tiendas: nuevasTiendas,
      hasMore: hasMore,
      loadedCount: loadedCount + nuevasTiendas.length
    };
  } catch (error) {
    console.error('Error fetching more tiendas específicas:', error);
    throw error;
  }
};

// Función optimizada para cargar solo datos esenciales inicialmente
export const fetchClientesYTiendasEsenciales = async (currentService, adminData = null) => {
  try {
    // Generar clave de caché
    const cacheKey = generateCacheKey(currentService.id, 'clientes_tiendas_esenciales', {
      adminId: adminData?.id || null,
      clientesAsignados: adminData?.clientes?.length || 0
    });

    // Intentar obtener del caché primero
    const cachedData = getFromCache(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    let clientesData = [];
    let tiendasData = [];
    let hasMoreClientes = false;
    let hasMoreTiendas = false;

    if (adminData && adminData.clientes && adminData.clientes.length > 0) {
      // Si tiene clientes asignados, cargar solo los primeros 10
      const clientesToLoad = adminData.clientes.slice(0, 10);
      
      const clientesRef = collection(db, 'servicios', currentService.id, 'clientes');
      const clientesPromises = clientesToLoad.map(clienteId => 
        getDoc(doc(clientesRef, clienteId))
      );
      const clientesDocs = await Promise.all(clientesPromises);
      
      clientesData = clientesDocs
        .filter(doc => doc.exists() && !doc.data().borrado)
        .map(doc => ({ id: doc.id, ...doc.data() }));

      // Cargar solo las tiendas de los primeros 5 clientes
      const clientesParaTiendas = adminData.clientes.slice(0, 5);
      const tiendasRef = collection(db, 'servicios', currentService.id, 'tiendas');
      const tiendasPromises = clientesParaTiendas.map(clienteId => 
        getDocs(query(tiendasRef, where('cliente', '==', clienteId), where('borrado', '==', false), limit(10)))
      );
      const tiendasSnapshots = await Promise.all(tiendasPromises);
      
      tiendasData = tiendasSnapshots.flatMap(snapshot => 
        snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      );

      hasMoreClientes = adminData.clientes.length > 10;
      hasMoreTiendas = adminData.clientes.length > 5 || tiendasData.length >= 50;
    } else {
      // Si no hay clientes asignados, cargar solo la primera página
      const [clientesResult, tiendasResult] = await Promise.all([
        fetchClientesPaginados(currentService, 20),
        fetchTiendasPaginadas(currentService, 20)
      ]);
      
      clientesData = clientesResult.clientes;
      tiendasData = tiendasResult.tiendas;
      hasMoreClientes = clientesResult.hasMore;
      hasMoreTiendas = tiendasResult.hasMore;
    }

    const result = {
      clientes: clientesData,
      tiendas: tiendasData,
      hasMoreClientes,
      hasMoreTiendas,
      totalClientes: adminData?.clientes?.length || 0,
      totalTiendas: 0 // No calculamos el total para evitar consultas costosas
    };
    
    // Guardar en caché
    setCache(cacheKey, result);
    
    return result;
  } catch (error) {
    console.error('Error fetching clientes y tiendas esenciales:', error);
    throw error;
  }
};